import React, { useState } from 'react';
import MarketingModal from '../MarketingModal';
import SettingToggleRow from '../settings/SettingToggleRow';
import type { CookieConsent } from '../../utils/cookieConsent';

interface CookiePreferencesModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Current decision (null = undecided → optional categories default off). */
    initial: CookieConsent | null;
    onSave: (choice: { analytics: boolean }) => void;
    onAcceptAll: () => void;
}

/**
 * Granular cookie preferences. Necessary cookies are always on (locked);
 * analytics is the only opt-in toggle. The form lives in an inner
 * component that mounts fresh each time the modal opens (MarketingModal only
 * renders children while open), so its state always seeds from the latest
 * `initial` without an effect.
 */
const CookiePreferencesModal: React.FC<CookiePreferencesModalProps> = ({
    isOpen,
    onClose,
    initial,
    onSave,
    onAcceptAll,
}) => (
    <MarketingModal
        isOpen={isOpen}
        onClose={onClose}
        title="Cookie preferences"
        maxWidth="580px"
        className="marketing-cookies"
    >
        <PreferencesForm
            initial={initial}
            onSave={onSave}
            onAcceptAll={onAcceptAll}
        />
    </MarketingModal>
);

const PreferencesForm: React.FC<{
    initial: CookieConsent | null;
    onSave: (choice: { analytics: boolean }) => void;
    onAcceptAll: () => void;
}> = ({ initial, onSave, onAcceptAll }) => {
    const [analytics, setAnalytics] = useState<boolean>(
        initial?.analytics ?? false,
    );

    return (
        <div className="cookie-prefs">
            <p className="cookie-prefs__intro">
                Choose which optional cookies Comtev may use. You can change
                this anytime from the Privacy &amp; Cookie Policy.
            </p>

            <div className="cookie-prefs__rows">
                <SettingToggleRow
                    title="Strictly necessary"
                    description="Required for the site to work — sign-in, security, and remembering your preferences. Always on."
                    checked
                    disabled
                    onChange={() => undefined}
                />
                <SettingToggleRow
                    title="Analytics"
                    description="Helps us understand how Comtev is used so we can improve it (Amplitude, low-rate session replay, and Sentry diagnostics)."
                    checked={analytics}
                    onChange={setAnalytics}
                />
            </div>

            <div className="marketing-cookie-actions">
                <button
                    type="button"
                    className="lp3-btn-primary marketing-modal-primary"
                    onClick={() => onSave({ analytics })}
                >
                    Save preferences
                </button>
                <button
                    type="button"
                    className="lp3-btn-secondary marketing-modal-secondary"
                    onClick={onAcceptAll}
                >
                    Accept all
                </button>
            </div>
        </div>
    );
};

export default CookiePreferencesModal;
